// index.ts
// Note: dotenv/config import is removed during build for browser compatibility
// Environment variables are injected at build time via rollup replace plugin

// Get API URL - check multiple sources
let API_URL: string | undefined;
if (typeof process !== 'undefined' && process.env) {
  API_URL = process.env.API_BASE_URL;
} else if (typeof window !== 'undefined') {
  API_URL = (window as any).API_BASE_URL;
}

/**
 * Verbose SDK logs (URLs, payloads). Off by default for production apps.
 * Node: `ACOUNT_SDK_DEBUG=1`  ·  Browser: `window.ACOUNT_SDK_DEBUG = true`
 */
function sdkDebugEnabled(): boolean {
  try {
    if (typeof process !== 'undefined' && process.env) {
      const v = process.env.ACOUNT_SDK_DEBUG;
      if (v === '1' || v === 'true') return true;
    }
    if (typeof window !== 'undefined' && (window as unknown as { ACOUNT_SDK_DEBUG?: boolean }).ACOUNT_SDK_DEBUG) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function sdkLog(...args: unknown[]): void {
  if (sdkDebugEnabled()) console.log(...args);
}

function sdkWarn(...args: unknown[]): void {
  if (sdkDebugEnabled()) console.warn(...args);
}

function sdkErr(...args: unknown[]): void {
  if (sdkDebugEnabled()) console.error(...args);
}

// Fallback to default if not set
if (!API_URL) {
  API_URL = 'https://api.acountpay.com';
  sdkWarn('API_BASE_URL not set, using default:', API_URL);
}

// Normalize API URL: remove trailing slashes and ensure /v1 is present only once
if (API_URL) {
  // Remove trailing slashes
  API_URL = API_URL.replace(/\/+$/, '');
  
  // Check if /v1 already exists in the URL (backend host URL might already include it)
  // Only add /v1 if it doesn't already exist
  if (!API_URL.includes('/v1')) {
    // Backend uses URI versioning with default version '1'
    API_URL = `${API_URL}/v1`;
  }
}

/** Normalize a user-supplied API host (e.g. from `new Account({ apiBaseUrl })`) */
function normalizeApiBaseUrl(raw: string): string {
  let u = raw.trim().replace(/\/+$/, '');
  if (!u.includes('/v1')) {
    u = `${u}/v1`;
  }
  return u;
}

const ENV = typeof process !== 'undefined' && process.env ? process.env.TOKEN_ENVIRONMENT : (typeof window !== 'undefined' ? (window as any).TOKEN_ENVIRONMENT : undefined); // "sandbox" or "production"

// TypeScript interfaces
export interface AccountLinkingPermissions {
  permissions: string[];
}

export interface AccountLinkingResponse {
  success: boolean;
  linkUrl?: string;
  authorizationUrl?: string;
  message?: string;
  consentId?: string;
  status?: string;
  errorMessage?: string;
  hasValidConsent?: boolean;
  remainingDays?: number;
  newConsentCreated?: boolean;
}

export interface AccountLinkingOptions {
  merchantId?: string; // Optional - kept for backward compatibility, but not used in URL
  userId: string;
  permissions: string[];
  // authorizationToken: string;
}

export interface LinkedAccount {
  accountId: string;
  accountName: string;
  accountType: string;
  bankName: string;
  bankId: string;
  accountNumber: string;
  accountHolderName: string;
  iban: string;
  bic?: string;
  balance?: number;
  currency: string;
  status: string;
  linkedAt: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetAccountsResponse {
  success: boolean;
  accounts: LinkedAccount[];
  message?: string;
}

export interface Transaction {
  transactionId: string;
  accountId: string;
  amount: number;
  currency: string;
  description: string;
  transactionType: string;
  date: string;
  balance: number;
  reference?: string;
}

export interface GetTransactionsResponse {
  success: boolean;
  transactions: Transaction[];
  message?: string;
}

export interface GetTransactionsOptions {
  merchantId?: string; // Optional - kept for backward compatibility, but not used in URL
  userId: string;
  accountId: string;
  fromDate: string;
  toDate: string;
}

export interface GetAccountBalanceOptions {
  merchantId?: string;
  userId: string;
  accountId: string;
}

export interface GetAccountBalanceResponse {
  success: boolean;
  balance?: {
    value: string;
    currency: string;
    lastUpdated: string;
  };
  accountDetails?: {
    iban: string;
    accountHolderName: string;
    bankId: string;
  };
  message?: string;
}

export interface AccountInformationRequest {
  merchantId: number;
  userId: string;
  accountId: string;
  accountHolderName: string;
  accountNumber: string;
  iban: string;
  bankName: string;
  bankId: string;
  bic?: string;
  currency: string;
  requestTimestamp: string;
  consentId?: string;
}

export interface StoreAccountInfoResponse {
  success: boolean;
  requestId?: string;
  message?: string;
}

// Predefined permission constants
export const ACCOUNT_PERMISSIONS = {
  READ_ACCOUNTS_DETAIL: "ReadAccountsDetail",
  READ_BALANCES: "ReadBalances",
  READ_TRANSACTIONS_DETAIL: "ReadTransactionsDetail",
} as const;

export type AccountPermission = typeof ACCOUNT_PERMISSIONS[keyof typeof ACCOUNT_PERMISSIONS];

export class Account {
  private clientId: string;
  /** When set, overrides module-level `API_URL` for this instance */
  private readonly apiBaseOverride?: string;

  constructor({ clientId, apiBaseUrl }: { clientId: string; apiBaseUrl?: string }) {
    if (!clientId) throw new Error("Client ID is required");
    this.clientId = clientId;
    if (apiBaseUrl) {
      this.apiBaseOverride = normalizeApiBaseUrl(apiBaseUrl);
    }
  }

  /**
   * Node / scripts: build from `process.env` (see `.env.example`).
   * - `ACOUNTPAY_CLIENT_ID` (required)
   * - `API_BASE_URL` (optional; same as global SDK config)
   */
  static fromEnv(overrides?: { clientId?: string; apiBaseUrl?: string }): Account {
    const clientId =
      overrides?.clientId ||
      (typeof process !== 'undefined' && process.env && process.env.ACOUNTPAY_CLIENT_ID) ||
      '';
    if (!clientId) {
      throw new Error(
        'ACOUNTPAY_CLIENT_ID is required (or pass { clientId } to fromEnv).',
      );
    }
    const rawBase =
      overrides?.apiBaseUrl ||
      (typeof process !== 'undefined' && process.env && process.env.API_BASE_URL) ||
      undefined;
    return new Account({
      clientId,
      apiBaseUrl: rawBase ? normalizeApiBaseUrl(rawBase) : undefined,
    });
  }

  /** Resolved API root including `/v1` */
  private getApiBase(): string {
    if (this.apiBaseOverride) return this.apiBaseOverride;
    if (!API_URL) {
      throw new Error(
        'API_BASE_URL is not set. Pass apiBaseUrl to new Account({ clientId, apiBaseUrl }) or set window.API_BASE_URL / process.env.API_BASE_URL.',
      );
    }
    return API_URL;
  }

  /**
   * Initiates a payment by creating it on your backend,
   * fetching the hosted‐pages URL, and redirecting the browser.
   */
  async initiatePayment({
    amount,
    referenceNumber,
  }: {
    amount: number;
    referenceNumber: string;
  }): Promise<void> {
    try {
      const paymentId = await this.#createPayment({ amount, referenceNumber });
      const { tokenRequestUrl } = await this.#getPaymentToken(paymentId);

      // Redirect the user to Token.io's hosted‐pages SCA flow
      window.location.assign(tokenRequestUrl);
    } catch (error: any) {
      sdkErr("Error initiating payment:", error);
      throw error;
    }
  }

  /**
   * Links a customer's bank account to enable access to account details,
   * balances, and transaction details.
   */
  async linkAccount({
    merchantId,
    userId,
    permissions,
  }: AccountLinkingOptions): Promise<AccountLinkingResponse> {
    try {
      // Use clientId (from constructor) instead of merchantId for the endpoint
      // The backend expects clientId (UUID) not merchantId (numeric ID)
      const url = `${this.getApiBase()}/sdk/users/${this.clientId}/accounts/link`;
      sdkLog('[linkAccount] url:', url);
      sdkLog('[linkAccount] apiBase:', this.getApiBase());
      sdkLog('[linkAccount] clientId:', this.clientId);
      sdkLog('[linkAccount] body:', { userId, permissions });
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          permissions,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        sdkErr('[linkAccount] HTTP error:', response.status, response.statusText);
        sdkErr('[linkAccount] response body:', errorText);
        throw new Error(`Account linking failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      sdkLog('API Response data:', data);
      
      // Handle case where valid consent already exists
      if (data.hasValidConsent && data.consent) {
        sdkLog('Valid consent found, using existing consent data');
        const consent = data.consent;
        return {
          success: true,
          authorizationUrl: consent.authorizationUrl,
          linkUrl: consent.authorizationUrl, // Keep for backward compatibility
          consentId: consent.consentId,
          status: consent.status,
          hasValidConsent: data.hasValidConsent,
          remainingDays: data.remainingDays,
          newConsentCreated: data.newConsentCreated,
          message: data.message || `Valid consent found with ${data.remainingDays} days remaining`,
        };
      }
      
    // Handle case where new consent is created (new structure with newConsent object)
      if (data.newConsentCreated && data.newConsent && data.newConsent.authorizationUrl) {
        sdkLog('New consent created, authorization URL found in newConsent object');
        const newConsent = data.newConsent;
        return {
          success: true,
          authorizationUrl: newConsent.authorizationUrl,
          linkUrl: newConsent.authorizationUrl, // Keep for backward compatibility
          consentId: newConsent.consentId,
          status: newConsent.status,
          hasValidConsent: false,
          newConsentCreated: true,
          message: data.message || "Account linking initiated successfully. Redirecting to authorization page...",
        };
      }
      
      // Handle case where new consent is created (old structure with direct authorizationUrl)
      if (data.authorizationUrl && data.authorizationUrl.trim() !== '') {
        sdkLog('New consent created, authorization URL found directly');
        return {
          success: true,
          authorizationUrl: data.authorizationUrl,
          linkUrl: data.authorizationUrl, // Keep for backward compatibility
          consentId: data.consentId,
          status: data.status,
          hasValidConsent: false,
          newConsentCreated: true,
          message: data.message || "Account linking initiated successfully. Redirecting to authorization page...",
        };
      }
      
      // No valid consent or authorization URL found
      sdkLog('No authorization URL or valid consent found, returning error');
      return {
        success: false,
        message: data.errorMessage || "No authorization URL received from the server",
      };
    } catch (error: any) {
      sdkErr("Error linking account:", error);
      return {
        success: false,
        message: error.message || "Failed to link account",
      };
    }
  }

  /**
   * Gets all linked accounts for a specific user.
   */
  async getLinkedAccounts({
    merchantId,
    userId,
  }: {
    merchantId?: string; // Optional - kept for backward compatibility, but not used in URL
    userId: string;
  }): Promise<GetAccountsResponse> {
    try {
      // Use clientId (from constructor) instead of merchantId for the endpoint
      // The backend expects clientId (UUID) not merchantId (numeric ID)
      const url = `${this.getApiBase()}/sdk/users/${this.clientId}/${userId}/accounts`;
      sdkLog('[getLinkedAccounts] url:', url);
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        sdkErr('[getLinkedAccounts] HTTP error:', response.status, response.statusText);
        sdkErr('[getLinkedAccounts] response body:', errorText);
        throw new Error(`Get accounts failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      // Map backend response to SDK format
      const accounts: LinkedAccount[] = (data.accounts || data || []).map((acc: any) => ({
        accountId: acc.accountId || acc.id,
        accountName: acc.accountName || acc.accountHolderName || "",
        accountType: acc.accountType || "",
        bankName: acc.bankName || "", // Will be populated by getBankName if needed
        bankId: acc.bankId || "",
        accountNumber: acc.accountNumber || acc.iban || "",
        accountHolderName: acc.accountHolderName || acc.accountName || "",
        iban: acc.iban || acc.accountNumber || "",
        bic: acc.bic || acc.accountDetails?.bic || acc.accountDetails?.accountDetails?.bic,
        balance: acc.balance,
        currency: acc.currency || "DKK",
        status: acc.status || acc.isActive ? "active" : "inactive",
        linkedAt: acc.linkedAt || acc.createdAt || new Date().toISOString(),
        createdAt: acc.createdAt,
        updatedAt: acc.updatedAt,
      }));

      return {
        success: true,
        accounts,
        message: data.message || "Accounts retrieved successfully",
      };
    } catch (error: any) {
      sdkErr("Error getting linked accounts:", error);
      return {
        success: false,
        accounts: [],
        message: error.message || "Failed to get linked accounts",
      };
    }
  }

  /**
   * Gets transactions for a specific account within a date range.
   */
  async getTransactions({
    merchantId,
    userId,
    accountId,
    fromDate,
    toDate,
  }: GetTransactionsOptions): Promise<GetTransactionsResponse> {
    try {
      // Use clientId (from constructor) instead of merchantId for the endpoint
      // The backend expects clientId (UUID) not merchantId (numeric ID)
      const response = await fetch(
        `${this.getApiBase()}/sdk/users/${this.clientId}/${userId}/accounts/${encodeURIComponent(accountId)}/transactions?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Get transactions failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const transactions = Array.isArray(data)
        ? data
        : data.transactions || [];
      return {
        success: true,
        transactions,
        message:
          data.message || "Transactions retrieved successfully",
      };
    } catch (error: any) {
      sdkErr("Error getting transactions:", error);
      return {
        success: false,
        transactions: [],
        message: error.message || "Failed to get transactions",
      };
    }
  }

  /**
   * Fetches current balance for an account from Token.io (`/accounts/{id}/balance` with consent).
   * Use this when `getLinkedAccounts` does not include `balance` (many banks omit it on the list call).
   */
  async getAccountBalance({
    userId,
    accountId,
  }: GetAccountBalanceOptions): Promise<GetAccountBalanceResponse> {
    try {
      const response = await fetch(
        `${this.getApiBase()}/sdk/users/${this.clientId}/${userId}/accounts/${encodeURIComponent(accountId)}/balance`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Get balance failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return {
        success: data.success !== false,
        balance: data.balance,
        accountDetails: data.accountDetails,
        message: data.message || "Balance retrieved successfully",
      };
    } catch (error: any) {
      sdkErr("Error getting account balance:", error);
      return {
        success: false,
        message: error.message || "Failed to get account balance",
      };
    }
  }

  /**
   * Gets bank name from bank ID
   */
  async getBankName(bankId: string): Promise<{ success: boolean; bankName?: string; message?: string }> {
    try {
      const response = await fetch(
        `${this.getApiBase()}/banks/bank-id/${encodeURIComponent(bankId)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Get bank name failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        bankName: data.bankName || null,
        message: "Bank name retrieved successfully",
      };
    } catch (error: any) {
      sdkErr("Error getting bank name:", error);
      return {
        success: false,
        message: error.message || "Failed to get bank name",
      };
    }
  }

  /**
   * Returns enriched account details (holder, IBAN, resolved bank name) for UI or server-backed flows.
   * Regulatory PDF generation is not exposed in this SDK.
   */
  async getAccountInformationForCompliance({
    merchantId,
    userId,
    accountId,
  }: {
    merchantId?: string; // Optional - kept for backward compatibility, but not used in URL
    userId: string;
    accountId?: string;
  }): Promise<{
    success: boolean;
    accountInfo?: AccountInformationRequest;
    accounts?: AccountInformationRequest[];
    message?: string;
  }> {
    try {
      // Get linked accounts
      const accountsResponse = await this.getLinkedAccounts({ merchantId, userId });

      if (!accountsResponse.success || !accountsResponse.accounts.length) {
        return {
          success: false,
          message: "No linked accounts found",
        };
      }

      // If accountId is provided, find that specific account
      let targetAccounts = accountsResponse.accounts;
      if (accountId) {
        targetAccounts = accountsResponse.accounts.filter(
          (acc) => acc.accountId === accountId
        );
        if (targetAccounts.length === 0) {
          return {
            success: false,
            message: `Account ${accountId} not found`,
          };
        }
      }

      // Process each account to get full information including bank name
      const accountInfoPromises = targetAccounts.map(async (account) => {
        // Get bank name from bankId
        // If bankName is missing, same as bankId, or looks like a bank ID (starts with "ngp-"), fetch the actual name
        let bankName = account.bankName;
        const needsBankNameFetch = !bankName || 
                                   bankName === account.bankId || 
                                   (bankName && bankName.startsWith('ngp-'));
        
        if (needsBankNameFetch && account.bankId) {
          sdkLog(`[getBankName] bankId=${account.bankId} currentLabel=${bankName}`);
          const bankResponse = await this.getBankName(account.bankId);
          if (bankResponse.success && bankResponse.bankName) {
            bankName = bankResponse.bankName;
            sdkLog(`[getBankName] resolved name: ${bankName}`);
          } else {
            sdkWarn(`Warning: bank name unavailable for ${account.bankId}; using bankId as label`);
            bankName = account.bankId; // Fallback to bankId if we can't get the name
          }
        }

        // Convert merchantId to number if provided (optional for display flows)
        const numericMerchantId = merchantId 
          ? (typeof merchantId === 'string' ? parseInt(merchantId, 10) : merchantId)
          : 0;

        return {
          merchantId: numericMerchantId,
          userId,
          accountId: account.accountId,
          accountHolderName: (account.accountHolderName || account.accountName || '').trim(),
          accountNumber: account.accountNumber || account.iban,
          iban: account.iban || account.accountNumber,
          bankName: (bankName || "Unknown Bank").trim(),
          bankId: account.bankId,
          bic: account.bic,
          currency: account.currency || 'DKK',
          requestTimestamp: new Date().toISOString(),
        } as AccountInformationRequest;
      });

      const accountInfos = await Promise.all(accountInfoPromises);

      // If single account requested, return single object
      if (accountId && accountInfos.length === 1) {
        return {
          success: true,
          accountInfo: accountInfos[0],
          message: "Account information retrieved successfully",
        };
      }

      // Return array of accounts
      return {
        success: true,
        accounts: accountInfos,
        message: "Account information retrieved successfully",
      };
    } catch (error: any) {
      sdkErr("Error getting enriched account information:", error);
      return {
        success: false,
        message: error.message || "Failed to get account information",
      };
    }
  }

  /**
   * Stores an account information request on the backend (audit / onboarding flows).
   */
  async storeAccountInformationRequest(
    accountInfo: AccountInformationRequest
  ): Promise<StoreAccountInfoResponse> {
    try {
      const response = await fetch(
        `${this.getApiBase()}/sdk/account-info-requests`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(accountInfo),
        }
      );

      if (!response.ok) {
        throw new Error(`Store account info failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        requestId: data.requestId || data.id,
        message: data.message || "Account information request stored successfully",
      };
    } catch (error: any) {
      sdkErr("Error storing account information request:", error);
      return {
        success: false,
        message: error.message || "Failed to store account information request",
      };
    }
  }

  /**
   * Create a payment on your backend.
   */
  async #createPayment({
    amount,
    referenceNumber,
  }: {
    amount: number;
    referenceNumber: string;
  }): Promise<string> {
    const response = await fetch(
      `${this.getApiBase()}/sdk/payment/${this.clientId}?amount=${amount}&referenceNumber=${referenceNumber}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Create payment failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.paymentId;
  }

  /**
   * Fetch the one-time hosted-pages link token (URL) from your backend.
   */
  async #getPaymentToken(paymentId: string): Promise<{ tokenRequestUrl: string }> {
    const response = await fetch(
      `${this.getApiBase()}/sdk/link-token/${this.clientId}?paymentId=${paymentId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Fetch link token failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      tokenRequestUrl: data.tokenRequestUrl,
    };
  }
}

// Export as default for backward compatibility
export default Account;
